import { Directive, Input, Output, EventEmitter, TemplateRef, ElementRef, Renderer2, HostListener, HostBinding, NgZone, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';

/** Cell template */
@Directive({
  selector: 'ng-template[rmsCellTemplate]',
  standalone: true
})
export class RmsCellTemplateDirective {
  @Input('rmsCellTemplate') key!: string;
  constructor(public tpl: TemplateRef<any>) { }
}

/** Row overlay template */
@Directive({
  selector: 'ng-template[rmsRowOverlay]',
  standalone: true
})
export class RmsRowOverlayDirective {
  @Input('rmsRowOverlay') when?: (row: any) => boolean;
  constructor(public tpl: TemplateRef<any>) { }
}
@Directive({
  selector: '[rmsAttrs]',
  standalone: true
})
export class RmsAttrsDirective implements OnChanges {
  @Input('rmsAttrs') attrs?: Record<string, any> | null;

  private prevKeys: string[] = [];

  constructor(private el: ElementRef<HTMLElement>, private r: Renderer2) { }

  ngOnChanges(_: SimpleChanges): void {
    // remove previously applied attributes
    for (const k of this.prevKeys) {
      this.r.removeAttribute(this.el.nativeElement, k);
    }
    this.prevKeys = [];

    const a = this.attrs || null;
    if (!a) return;

    // set new attributes
    for (const [k, v] of Object.entries(a)) {
      if (v === false || v == null) continue;
      // boolean true => empty attribute
      this.r.setAttribute(this.el.nativeElement, k, v === true ? '' : String(v));
      this.prevKeys.push(k);
    }
  }
}
/* =========================
 * Column Resize (pointer events)
 * ========================= */
@Directive({
  selector: '[rmsColumnResize]',
  standalone: true
})
export class RmsColumnResizeDirective implements OnInit, OnDestroy {
  @Input() columnKey!: string;
  @Output() columnResizeStart = new EventEmitter<{ key: string; clientX: number }>();
  @Output() columnResizeMove = new EventEmitter<{ key: string; clientX: number }>();
  @Output() columnResizeEnd = new EventEmitter<{ key: string }>();
  @Output() columnResize = new EventEmitter<{ key: string; deltaPx: number }>(); // compat

  private handle!: HTMLElement;
  private removePointerDown?: () => void;
  private activePointerId: number | null = null;
  private lastX = 0;

  constructor(private el: ElementRef<HTMLElement>, private r: Renderer2, private zone: NgZone) { }

  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.handle = this.r.createElement('span');
    this.r.addClass(this.handle, 'rms-col-resize-handle');
    this.r.appendChild(host, this.handle);

    this.removePointerDown = this.r.listen(this.handle, 'pointerdown', (ev: PointerEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.activePointerId = ev.pointerId;
      this.lastX = ev.clientX;
      (this.handle as any).setPointerCapture?.(ev.pointerId);
      document.body.classList.add('is-resizing');

      this.columnResizeStart.emit({ key: this.columnKey, clientX: ev.clientX });

      const onMove = (e: PointerEvent) => {
        if (this.activePointerId !== e.pointerId) return;
        const dx = e.clientX - this.lastX;
        this.lastX = e.clientX;
        this.columnResizeMove.emit({ key: this.columnKey, clientX: e.clientX });
        this.columnResize.emit({ key: this.columnKey, deltaPx: dx });
      };
      const onUp = (e: PointerEvent) => {
        if (this.activePointerId !== e.pointerId) return;
        (this.handle as any).releasePointerCapture?.(e.pointerId);
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onUp, true);
        this.activePointerId = null;
        document.body.classList.remove('is-resizing');
        this.columnResizeEnd.emit({ key: this.columnKey });
      };

      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onUp, true);
    });
  }

  ngOnDestroy(): void {
    this.removePointerDown?.();
    document.body.classList.remove('is-resizing');
  }
}

/* =========================
 * Column Drag (custom ghost + begin/end)
 * ========================= */
@Directive({
  selector: '[rmsColumnDrag]',
  standalone: true
})
export class RmsColumnDragDirective implements OnInit, OnDestroy {
  @Input() dragIndex!: number;
  @Input() dragKey?: string;
  @Input() dragLabel?: string;

  @Output() dragBegin = new EventEmitter<{ visibleIndex: number }>();
  @Output() dragEnd = new EventEmitter<{ visibleIndex: number }>();

  private ghostEl?: HTMLElement;

  constructor(private el: ElementRef<HTMLElement>, private r: Renderer2) { }

  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.r.setAttribute(host, 'draggable', 'true');
    this.r.addClass(host, 'rms-col-drag-handle');
  }

  @HostListener('dragstart', ['$event'])
  onDragStart(ev: DragEvent) {
    if (!ev.dataTransfer) return;

    // index in multiple flavors (browser quirks)
    if (Number.isInteger(this.dragIndex)) {
      const idx = String(this.dragIndex);
      ev.dataTransfer.setData('text/rms-col-index', idx);
      ev.dataTransfer.setData('text/plain', idx);
      ev.dataTransfer.setData('text', idx);
    }
    if (this.dragKey) ev.dataTransfer.setData('text/rms-col-key', this.dragKey);
    ev.dataTransfer.effectAllowed = 'move';

    // custom ghost
    this.ghostEl = document.createElement('div');
    Object.assign(this.ghostEl.style, {
      position: 'fixed', top: '0', left: '0', padding: '6px 10px',
      background: 'white', border: '1px solid rgba(0,0,0,.2)', borderRadius: '6px',
      boxShadow: '0 6px 16px rgba(0,0,0,.25)', fontFamily: 'inherit', fontSize: '12px',
      color: '#333', zIndex: '99999', pointerEvents: 'none'
    } as CSSStyleDeclaration);
    this.ghostEl.innerHTML = `
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
      <span class="material-icons" style="font-size:16px;">drag_indicator</span>
      <span>Move Column</span>
    </div>
    <div style="font-weight:700;">${(this.dragLabel ?? this.dragKey ?? '')}</div>
  `;
    document.body.appendChild(this.ghostEl);
    ev.dataTransfer.setDragImage(this.ghostEl, 10, 10);

    // 🔹 CRITICAL: let gap zones get pointer hits (handles won’t intercept)
    document.body.classList.add('is-col-dragging');

    this.dragBegin.emit({ visibleIndex: this.dragIndex });
  }

  @HostListener('dragend')
  onDragEndInternal() {
    this.dragEnd.emit({ visibleIndex: this.dragIndex });

    // cleanup ghost
    if (this.ghostEl && this.ghostEl.parentNode) {
      this.ghostEl.parentNode.removeChild(this.ghostEl);
      this.ghostEl = undefined;
    }

    // 🔹 re-enable handles
    document.body.classList.remove('is-col-dragging');
  }
  ngOnDestroy(): void {
    if (this.ghostEl && this.ghostEl.parentNode) {
      this.ghostEl.parentNode.removeChild(this.ghostEl);
    }
  }
}

/* =========================
 * GAP DROP ZONES (between columns only)
 * - Left gap (before first or before current th)
 * - Right gap (between current th and next)
 * ========================= */
@Directive()
abstract class BaseGapDrop {
  @Input() gapIndex!: number; // insert at this visible index
  @Output() columnReorder = new EventEmitter<{ fromIndex: number; toIndex: number }>();

  protected zone!: HTMLElement;
  protected destroyers: Array<() => void> = [];

  constructor(protected host: ElementRef<HTMLElement>, protected r: Renderer2, private side: 'left' | 'right') { }

  ngOnInitBase() {
    const th = this.host.nativeElement;
    this.zone = this.r.createElement('span');
    this.r.addClass(this.zone, 'gap-drop-zone');
    this.r.addClass(this.zone, this.side === 'left' ? 'left' : 'right');
    this.r.appendChild(th, this.zone);

    const allow = (dt: DataTransfer | null | undefined) => {
      if (!dt) return false;
      const types = Array.from(dt.types || []);
      return types.includes('text/rms-col-index') || types.includes('text/plain') || types.includes('text');
    };

    const overLike = (ev: DragEvent) => {
      if (allow(ev.dataTransfer)) {
        ev.preventDefault(); // required to allow drop
        this.zone.classList.add('active');
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
      }
    };

    const leave = () => this.zone.classList.remove('active');

    const drop = (ev: DragEvent) => {
      ev.preventDefault();
      this.zone.classList.remove('active');

      const raw = ev.dataTransfer?.getData('text/rms-col-index')
        || ev.dataTransfer?.getData('text/plain')
        || ev.dataTransfer?.getData('text');
      const fromIndex = raw != null ? parseInt(raw, 10) : NaN;
      if (Number.isInteger(fromIndex)) {
        this.columnReorder.emit({ fromIndex, toIndex: this.gapIndex });
      }
    };

    this.zone.addEventListener('dragenter', overLike);
    this.zone.addEventListener('dragover', overLike);
    this.zone.addEventListener('dragleave', leave);
    this.zone.addEventListener('drop', drop);

    this.destroyers.push(() => this.zone.removeEventListener('dragenter', overLike));
    this.destroyers.push(() => this.zone.removeEventListener('dragover', overLike));
    this.destroyers.push(() => this.zone.removeEventListener('dragleave', leave));
    this.destroyers.push(() => this.zone.removeEventListener('drop', drop));
  }

  ngOnDestroyBase() { this.destroyers.forEach(fn => fn()); }
}

@Directive({
  selector: '[rmsGapDropLeft]',
  standalone: true
})
export class RmsGapDropLeftDirective extends BaseGapDrop implements OnInit, OnDestroy {
  constructor(host: ElementRef<HTMLElement>, r: Renderer2) { super(host, r, 'left'); }
  ngOnInit() { this.ngOnInitBase(); }
  ngOnDestroy() { this.ngOnDestroyBase(); }
}

@Directive({
  selector: '[rmsGapDropRight]',
  standalone: true
})
export class RmsGapDropRightDirective extends BaseGapDrop implements OnInit, OnDestroy {
  constructor(host: ElementRef<HTMLElement>, r: Renderer2) { super(host, r, 'right'); }
  ngOnInit() { this.ngOnInitBase(); }
  ngOnDestroy() { this.ngOnDestroyBase(); }
}

/* =========================
 * Trash Drop Zone
 * ========================= */
@Directive({
  selector: '[rmsTrashDrop]',
  standalone: true
})
export class RmsTrashDropDirective {
  @Output() columnHide = new EventEmitter<{ key?: string; index?: number }>();
  @HostBinding('class.rms-trash-active') active = false;

  @HostListener('dragover', ['$event'])
  onDragOver(ev: DragEvent) {
    if (ev.dataTransfer?.types.some(t => t === 'text/rms-col-index' || t === 'text/rms-col-key')) {
      ev.preventDefault();
      this.active = true;
      ev.dataTransfer.dropEffect = 'move';
    }
  }

  @HostListener('dragleave')
  onLeave() {
    this.active = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.active = false;

    // Prefer key if present
    const key = ev.dataTransfer?.getData('text/rms-col-key');
    if (key) {
      this.columnHide.emit({ key });
      return;
    }

    // Fallback to visible index
    const idx = ev.dataTransfer?.getData('text/rms-col-index');
    if (idx != null && idx !== '') {
      const index = parseInt(idx, 10);
      if (Number.isInteger(index)) {
        this.columnHide.emit({ index });
      }
    }
  }
}

/* =========================
 * Convenience export array
 * ========================= */
export const RMS_TABLE_DIRECTIVES = [
  RmsCellTemplateDirective,
  RmsRowOverlayDirective,
  RmsColumnResizeDirective,
  RmsColumnDragDirective,
  RmsGapDropLeftDirective,
  RmsGapDropRightDirective,
  RmsTrashDropDirective,
  RmsAttrsDirective
] as const;
